import React, { createContext, useContext, useState, useEffect } from 'react';

// Import all locale files
import en from '../locales/en.json';
import ptBR from '../locales/pt-BR.json';
import es from '../locales/es.json';
import fr from '../locales/fr.json';
import ru from '../locales/ru.json';
import zh from '../locales/zh.json';
import fa from '../locales/fa.json';
import ar from '../locales/ar.json';

const locales = { en, 'pt-BR': ptBR, es, fr, ru, zh, fa, ar };

const I18nContext = createContext();

// RTL languages
const RTL_LANGUAGES = ['ar', 'fa', 'ps'];

// Map a raw navigator.language (e.g. "pt-BR", "pt", "zh-CN", "fa-IR") to one of
// our supported locales. Returns null if nothing reasonable matches.
function detectBrowserLocale() {
  if (typeof navigator === 'undefined') return null;
  const supported = Object.keys(locales);
  const candidates = [navigator.language, ...(navigator.languages || [])].filter(Boolean);

  for (const raw of candidates) {
    if (!raw) continue;
    // Exact match, case-insensitive (handles "pt-BR" exactly).
    const exact = supported.find((s) => s.toLowerCase() === raw.toLowerCase());
    if (exact) return exact;

    const base = raw.split('-')[0].toLowerCase();
    // Chinese variants → zh (Simplified) is our only Chinese locale.
    if (base === 'zh') return 'zh';
    // Portuguese variants → pt-BR (only Portuguese we have).
    if (base === 'pt') return 'pt-BR';
    // Farsi / Persian aliases.
    if (base === 'fa' || base === 'per' || base === 'prs') return 'fa';
    const baseMatch = supported.find((s) => s.split('-')[0].toLowerCase() === base);
    if (baseMatch) return baseMatch;
  }
  return null;
}

export function I18nProvider({ children }) {
  const [locale, setLocale] = useState(() => {
    const saved = localStorage.getItem('interfaceLanguage');
    if (saved && locales[saved]) return saved;
    // First visit — try to guess from the browser.
    const detected = detectBrowserLocale();
    return detected || 'en';
  });

  const [translations, setTranslations] = useState(locales[locale] || locales.en);

  useEffect(() => {
    setTranslations(locales[locale] || locales.en);
    localStorage.setItem('interfaceLanguage', locale);
    
    // Set document direction for RTL languages
    document.documentElement.dir = RTL_LANGUAGES.includes(locale) ? 'rtl' : 'ltr';
    document.documentElement.lang = locale;
  }, [locale]);

  // Translation function with interpolation support
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        // Fallback to English
        value = locales.en;
        for (const k2 of keys) {
          value = value?.[k2];
          if (value === undefined) break;
        }
        break;
      }
    }

    if (typeof value !== 'string') {
      return key; // Return key if translation not found
    }

    // Replace interpolation params {{param}}
    return value.replace(/\{\{(\w+)\}\}/g, (_, param) => params[param] || '');
  };

  // Raw getter: returns the translation value as-is (string, array, object, or undefined)
  const tRaw = (key) => {
    const keys = key.split('.');
    let value = translations;
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        value = locales.en;
        for (const k2 of keys) {
          value = value?.[k2];
          if (value === undefined) return undefined;
        }
        return value;
      }
    }
    return value;
  };

  const isRTL = RTL_LANGUAGES.includes(locale);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, tRaw, isRTL, availableLocales: Object.keys(locales) }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

export default I18nContext;
